/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router';
import { SpaceListPage } from './SpacesPage/SpaceListPage';
import { AppRoutes } from '@/constants/routes';
import { SpaceDetailPage } from './SpacesPage/SpaceDetailPage';
import { useBreadCrumbs, useProjectBreadCrumbLink } from '@/hooks/useBreadCrumbs';
import { AnchorDiv, CBreadCrumbs } from '@/components';
import { useProjectPath } from '@/hooks/useProjectPath';

export const SpacesPage: React.FC = () => {
  const { pathname } = useLocation();
  const { toBrowserProjectPath } = useProjectPath();
  const { homeLink, spacesListLink } = useBreadCrumbs();
  const { projectLink, setMeta, meta, reset } = useProjectBreadCrumbLink();

  const breadCrumbModelLinks = useMemo(() => {
    const model = [spacesListLink];
    if (projectLink) {
      model.push(projectLink);
    }
    return model;
  }, [projectLink, spacesListLink]);

  useEffect(() => {
    if (meta?.link) {
      const browserPath = toBrowserProjectPath(meta.link);
      if (pathname !== AppRoutes.spaces.detail.url(browserPath)) {
        reset();
      }
    }
  }, [pathname, toBrowserProjectPath, meta, reset]);

  return (
    <AnchorDiv className="w-full py-1 h-full">
      <CBreadCrumbs className="my-2" home={homeLink} model={breadCrumbModelLinks} />
      <Routes>
        <Route index element={<SpaceListPage />} />
        <Route path={AppRoutes.spaces.detail.path} element={<SpaceDetailPage setMeta={setMeta} />} />
      </Routes>
    </AnchorDiv>
  );
};
